import { Vue, Component, Prop } from 'vue-property-decorator';
import { PricingTableHelper } from '@lib-shared/editor/helpers/pricing-table.helper';
import {
  IPricingTableColumn,
  IPricingTableCurrencySettings,
  IPricingTableSummaryValues,
} from '@lib-shared/editor/types';
import { translate } from '@lib-shared/helpers/translation.helper';
import { EPricingTableColumn } from '@lib-shared/editor/types/enums';

@Component
export default class PricingTablePriceSummaryRoot extends Vue {
  @Prop() summaryValues: IPricingTableSummaryValues;
  @Prop() sectionSummary: IPricingTableSummaryValues;
  @Prop() columns: IPricingTableColumn[];
  @Prop() currencySettings: IPricingTableCurrencySettings;
  @Prop() isPreCalculated: boolean;

  get subtotalDiscount(): string {
    if (this.subtotalDiscountIsEmpty) {
      return translate('add-discount');
    }
    return `-${PricingTableHelper.currencyFormat(
      this.sectionSummary.discount.value,
      this.currencySettings
    )}`;
  }

  get totalPriceValue(): string {
    return this.summaryValues.price.value;
  }

  get subtotalValue(): string {
    return this.sectionSummary.price.value;
  }

  get subtotalEnabled(): boolean {
    return this.sectionSummary.price.enabled;
  }

  get subtotalDiscountValue(): string {
    return this.sectionSummary.discount.value;
  }

  get subtotalDiscountIsEmpty(): boolean {
    return this.subtotalDiscountValue === '0' || this.subtotalDiscountValue === '';
  }

  get subtotalTaxValue(): string {
    return this.sectionSummary.tax.value;
  }

  get subtotalTaxIsEmpty(): boolean {
    return this.subtotalTaxValue === '0' || this.subtotalTaxValue === '';
  }

  get subtotalDiscountEnabled(): boolean {
    return this.columns.find(column => column.name === EPricingTableColumn.Discount)?.enabled;
  }

  get subtotalTaxEnabled(): boolean {
    return this.columns.find(column => column.name === EPricingTableColumn.Tax)?.enabled;
  }

  get showSubtotalDiscount(): boolean {
    if (!this.subtotalDiscountEnabled) {
      return false;
    }
    return !this.subtotalDiscountIsEmpty;
  }

  get showSubtotalTax(): boolean {
    if (!this.subtotalTaxEnabled) {
      return false;
    }
    return !this.subtotalTaxIsEmpty;
  }

  get subtotalTax(): string {
    return this.subtotalTaxValue === '' || this.subtotalTaxValue === '0'
      ? null
      : this.subtotalTaxValue;
  }

  get totalDiscountIsEmpty(): boolean {
    const totalDiscountValue = this.summaryValues.discount.value;
    return totalDiscountValue === '0' || totalDiscountValue === '';
  }

  get totalTaxIsEmpty(): boolean {
    const totalTaxValue = this.summaryValues.tax.value;
    return totalTaxValue === '0' || totalTaxValue === '';
  }

  get totalDiscount(): string {
    const { discount: totalDiscount } = this.summaryValues;
    const discountInCurrency = String(
      (Number(this.subtotalValue) * Number(totalDiscount.value)) / 100
    );
    return totalDiscount.flatFee
      ? `-${PricingTableHelper.currencyFormat(totalDiscount.value, this.currencySettings)}`
      : `-${PricingTableHelper.currencyFormat(discountInCurrency, this.currencySettings)} (${
          totalDiscount.value
        }%)`;
  }

  get totalTax(): string {
    const { tax: totalTax, discount: totalDiscount } = this.summaryValues;
    const totalDiscountSum = totalDiscount.flatFee
      ? totalDiscount.value
      : (Number(this.subtotalValue) * Number(totalDiscount.value)) / 100;
    const subtotalAfterTotalDiscount = Number(this.subtotalValue) - Number(totalDiscountSum);
    const taxInCurrency = String((subtotalAfterTotalDiscount * Number(totalTax.value)) / 100);
    return totalTax.flatFee
      ? PricingTableHelper.currencyFormat(totalTax.value, this.currencySettings)
      : `${PricingTableHelper.currencyFormat(taxInCurrency, this.currencySettings)} (${
          totalTax.value
        }%)`;
  }
}
